import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

const COOLDOWN_MS = 60 * 1000;

// Stored as epoch millis rather than a serialized DateTime: a custom
// serializer/deserializer pair only has to round-trip wrong once to produce an
// invalid date, which reads as "no cooldown" and re-enables the button.
const calculateEndTime = (sentAtMs?: number): number | null => {
  if (!sentAtMs) return null;

  const endTime = sentAtMs + COOLDOWN_MS;
  return endTime > Date.now() ? endTime : null;
};

const formatTime = (ms: number) => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Resend cooldown for the login code step, persisted so a page reload does not
 * hand back a Resend button the server will ignore.
 *
 * Keyed per address: editing the email should not inherit the previous
 * address's cooldown.
 */
export const useCodeResendCooldown = (email: string) => {
  const [sentAtMs, setSentAtMs, removeSentAtMs] = useLocalStorage<number>(
    `_ah_login_code_sent_at:${email}`
  );

  const [endTime, setEndTime] = useState<number | null>(() =>
    calculateEndTime(sentAtMs)
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    const end = calculateEndTime(sentAtMs);
    return end ? Math.max(0, end - Date.now()) : 0;
  });

  useEffect(() => {
    const next = calculateEndTime(sentAtMs);
    setEndTime(next);

    // A lapsed record is dead weight, and the key is per address - left in place
    // they accumulate in localStorage one per email ever typed on this device.
    if (sentAtMs && !next) {
      removeSentAtMs();
    }
  }, [sentAtMs, removeSentAtMs]);

  useEffect(() => {
    if (!endTime) return;

    const interval = setInterval(() => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        setEndTime(null);
        setTimeRemaining(0);
        removeSentAtMs();
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endTime, removeSentAtMs]);

  const markSent = useCallback(() => {
    const now = Date.now();
    setSentAtMs(now);
    setEndTime(now + COOLDOWN_MS);
    setTimeRemaining(COOLDOWN_MS);
  }, [setSentAtMs]);

  return {
    canResend: !endTime,
    timeRemaining,
    formatTime,
    markSent,
  };
};
